/* ================================================================
   LFJ HOME ROOM — Checkpoint 58 (free-roam engine restored)
   ----------------------------------------------------------------
   Puts the drag-canvas + spider-web room back (ported from the
   checkpoint-28 engine) and carries forward everything built since:
   the multi-source theater player modal, the taskbar + "latest drop"
   widget, the notify toast, and the WHO / WHAT / BRING content —
   which stays in a rail column beside the canvas (checkpoint 28's
   design), now sized inside the screen instead of pinned to the
   browser edge.

   What the engine does:
   1. SCATTER — every node (hero TV, video cards, statement/tagline,
      and anything marked data-lfj-static) is placed at random around the
      hero on each page load. Unlike the checkpoint-28 version, which
      only checked centre-to-centre distance, placement here checks
      real rectangles (with a margin), so large nodes can't overlap.
   2. WEB — K-nearest-neighbour SVG lines (K=3), classified by the
      kind of nodes they connect (feature / related / context /
      thread), styled in CSS.
   3. PAN — pointer-events drag (mouse, touch, pen) with momentum.
      A drag can start on ANY node now (a click is only swallowed if
      the pointer actually moved), and touch works the same as mouse.
   4. ZOOM (new) — eased zoom anchored on the cursor / pinch point:
      mouse wheel, trackpad pinch (ctrl+wheel), two-finger pinch,
      + / - buttons, keyboard (+ - 0, arrows to pan).
   5. CONTENT — video cards from videos.json (LFJ-destined entries
      first, then anything with a thumbnail), click → modal via the
      shared LFJ_THEATER_PLAYER engine.

   THE "UPPER HALF ONLY" BUG (root cause, fixed here + in the CSS):
   the old viewport was `height: calc(100% - 80px)` inside a parent
   with no definite height, so it silently fell back to its 400px
   min-height — a short window inside a much taller screen. The
   camera clamp then only ever "saw" that short window. The CSS now
   sizes the whole chain with flex (see lfj-lfj.css), and this file
   re-measures the viewport with a ResizeObserver and re-clamps the
   camera whenever it changes, so a wrong size can never persist.
================================================================ */
(function () {
  if (window.__lfjLfjBound) return;
  window.__lfjLfjBound = true;

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }

  function safeRun(label, fn) {
    try { return fn(); }
    catch (e) { console.warn('[LFJ:lfj] ' + label, e); }
  }

  function escHTML(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function escAttr(s) { return escHTML(s).replace(/'/g, '&#39;'); }
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  /* ── Constants ─────────────────────────────────────────────── */
  var CANVAS_W = 4200;
  var CANVAS_H = 3200;
  var CX = 2100;              /* cluster centre = hero centre */
  var CY = 1600;
  var RX = 1150;              /* scatter radii (canvas is much bigger — room to roam) */
  var RY = 900;
  var INITIAL_ZOOM = 0.8;     /* opening view: wide enough that the web is visible at a glance */
  var NODE_MARGIN = 70;       /* min px gap between any two node rectangles */
  var CONNECT_K = 3;          /* nearest neighbours per node for the web */
  var MAX_CARDS = 20;         /* max video cards on the canvas */
  var NOTIFY_DELAY_MS_DEFAULT = 5000;

  var ZOOM_MAX = 2.2;
  var ZOOM_FLOOR = 0.25;      /* never zoom out past this even on huge screens */
  var ZOOM_STEP = 1.25;       /* per press of + / - */
  var ZOOM_TAU_MS = 90;       /* zoom easing time-constant (lower = snappier) */
  var SLACK = 100;            /* px of over-scroll past the canvas edge */

  var reducedMotion = false;
  try {
    reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) {}

  /* ================================================================
     ROOM: canvas, camera, nodes, modal
  ================================================================ */
  onReady(function () {
    safeRun('lfj init', function () {
      var rootEl   = document.getElementById('lfjLfjRoot');
      var viewport = document.getElementById('lfjLfjViewport');
      var stageEl  = document.getElementById('lfjLfjStage') || rootEl;
      var canvas   = document.getElementById('lfjLfjCanvas');
      var webSvg   = document.getElementById('lfjLfjWeb');
      var nodesEl  = document.getElementById('lfjLfjNodes');
      var heroEl   = document.getElementById('homeBgVideoWrap');
      var hintEl   = document.getElementById('lfjLfjHint');
      var zoomPct  = document.getElementById('lfjZoomPct');
      var modal      = document.getElementById('lfjLfjModal');
      var modalBody  = document.getElementById('lfjLfjModalContent');
      var modalClose = document.getElementById('lfjLfjModalClose');
      var modalTitle = document.getElementById('lfjLfjModalTitle');
      var modalYear  = document.getElementById('lfjLfjModalYear');
      if (!rootEl || !viewport || !canvas || !nodesEl) return;

      var NOTIFY_DELAY_MS = (parseInt(rootEl.dataset.notifyDelay, 10) || 5) * 1000 || NOTIFY_DELAY_MS_DEFAULT;

      /* ── Camera ───────────────────────────────────────────────
         Screen position of a canvas point = cam.{x,y} + point * cam.z
         (canvas has transform-origin 0 0). */
      var cam = { x: 0, y: 0, z: 1 };
      var zTarget = 1;
      var anchor = null;          /* {sx,sy,wx,wy} — zoom pivot kept fixed on screen */
      var velX = 0, velY = 0;     /* px/ms, drives momentum */
      var fly = null;             /* eased camera move (HOME button, focus) */
      var raf = 0, lastT = 0, idleTimer = 0;
      var cameraReady = false;

      function vp() {
        return { w: viewport.clientWidth, h: viewport.clientHeight };
      }

      /* Lowest zoom that still lets the canvas cover the viewport, so
         you can never zoom out into empty space. */
      function minZoom() {
        var s = vp();
        if (!s.w || !s.h) return ZOOM_FLOOR;
        return Math.max(ZOOM_FLOOR, s.w / CANVAS_W, s.h / CANVAS_H);
      }

      /* Where the camera would end up if asked to sit at (x, y, z) —
         same rules as the live clamp, without touching the live camera. */
      function clampedDest(x, y, z) {
        var s = vp();
        var W = CANVAS_W * z, H = CANVAS_H * z;
        var minX = s.w - W - SLACK, maxX = SLACK;
        var minY = s.h - H - SLACK, maxY = SLACK;
        /* If the canvas is smaller than the window on an axis, centre it. */
        return {
          x: minX > maxX ? (s.w - W) / 2 : clamp(x, minX, maxX),
          y: minY > maxY ? (s.h - H) / 2 : clamp(y, minY, maxY),
          z: z
        };
      }

      function clampCam() {
        var d = clampedDest(cam.x, cam.y, cam.z);
        cam.x = d.x; cam.y = d.y;
      }

      function applyCam() {
        var x = Math.round(cam.x * 100) / 100;
        var y = Math.round(cam.y * 100) / 100;
        canvas.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) scale(' + cam.z + ')';
        if (zoomPct) zoomPct.textContent = Math.round(cam.z * 100) + '%';
      }

      function markMoving() {
        canvas.classList.add('is-moving');
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function () { canvas.classList.remove('is-moving'); }, 220);
      }

      function kick() {
        markMoving();
        if (!raf) { lastT = performance.now(); raf = requestAnimationFrame(tick); }
      }

      function tick(now) {
        raf = 0;
        var dt = Math.min(48, now - lastT);
        lastT = now;
        var again = false;

        /* Eased camera fly (HOME / focus) */
        if (fly) {
          var p = fly.dur > 0 ? Math.min(1, (now - fly.t0) / fly.dur) : 1;
          var e = 1 - Math.pow(1 - p, 3);
          cam.x = fly.from.x + (fly.to.x - fly.from.x) * e;
          cam.y = fly.from.y + (fly.to.y - fly.from.y) * e;
          cam.z = fly.from.z + (fly.to.z - fly.from.z) * e;
          zTarget = cam.z;
          if (p >= 1) fly = null; else again = true;
        } else {
          /* Smooth zoom toward zTarget, holding the anchor still on screen */
          if (Math.abs(zTarget - cam.z) > 0.0005) {
            var a = reducedMotion ? 1 : 1 - Math.exp(-dt / ZOOM_TAU_MS);
            var nz = cam.z + (zTarget - cam.z) * a;
            if (Math.abs(zTarget - nz) < 0.0005) nz = zTarget;
            if (anchor) {
              cam.x = anchor.sx - anchor.wx * nz;
              cam.y = anchor.sy - anchor.wy * nz;
            }
            cam.z = nz;
            again = true;
          } else if (cam.z !== zTarget) {
            cam.z = zTarget;
          }

          /* Momentum after a flick */
          if (velX || velY) {
            cam.x += velX * dt;
            cam.y += velY * dt;
            var bx = cam.x, by = cam.y;
            clampCam();
            if (cam.x !== bx) velX = 0;     /* hit an edge — stop pushing */
            if (cam.y !== by) velY = 0;
            var decay = Math.pow(0.94, dt / 16.67);
            velX *= decay; velY *= decay;
            if (Math.abs(velX) < 0.02) velX = 0;
            if (Math.abs(velY) < 0.02) velY = 0;
            if (velX || velY) again = true;
          }
        }

        clampCam();
        applyCam();
        if (again) { markMoving(); raf = requestAnimationFrame(tick); }
      }

      function stopMotion() { velX = 0; velY = 0; fly = null; }

      function hideHint() { if (hintEl) hintEl.classList.add('is-gone'); }

      /* Zoom to an absolute level, pivoting on screen point (sx, sy). */
      function zoomTo(z, sx, sy) {
        var s = vp();
        if (sx == null) sx = s.w / 2;
        if (sy == null) sy = s.h / 2;
        zTarget = clamp(z, minZoom(), ZOOM_MAX);
        anchor = { sx: sx, sy: sy, wx: (sx - cam.x) / cam.z, wy: (sy - cam.y) / cam.z };
        stopMotion();
        hideHint();
        kick();
      }

      function flyTo(x, y, z, dur) {
        var target = clampedDest(x, y, clamp(z, minZoom(), ZOOM_MAX));
        velX = 0; velY = 0; anchor = null;
        fly = { from: { x: cam.x, y: cam.y, z: cam.z }, to: target, t0: performance.now(), dur: reducedMotion ? 0 : (dur == null ? 520 : dur) };
        kick();
      }

      /* Camera position that puts canvas point (px,py) mid-screen at zoom z */
      function camFor(px, py, z) {
        var s = vp();
        return { x: s.w / 2 - px * z, y: s.h / 2 - py * z };
      }

      function goHome() {
        var z = clamp(INITIAL_ZOOM, minZoom(), ZOOM_MAX);
        var t = camFor(CX, CY, z);
        flyTo(t.x, t.y, z);
        hideHint();
      }

      function initCamera() {
        var s = vp();
        if (!s.w || !s.h) return false;
        cam.z = zTarget = clamp(INITIAL_ZOOM, minZoom(), ZOOM_MAX);
        var t = camFor(CX, CY, cam.z);
        cam.x = t.x; cam.y = t.y;
        clampCam();
        applyCam();
        cameraReady = true;
        return true;
      }

      /* Re-measure whenever the viewport's real size changes — this is
         what makes a collapsed / late-sized parent unable to strand the
         camera against a stale height. */
      function onViewportResize() {
        if (!cameraReady) { initCamera(); return; }
        if (zTarget < minZoom()) zTarget = minZoom();
        if (cam.z < minZoom()) cam.z = minZoom();
        clampCam();
        applyCam();
      }
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(function () { safeRun('resize', onViewportResize); }).observe(viewport);
      }
      window.addEventListener('resize', function () { safeRun('resize', onViewportResize); });

      /* Focus on an off-screen button makes the browser scroll the
         (overflow:hidden) viewport itself — which would knock the
         canvas out of alignment. Pin it back. */
      viewport.addEventListener('scroll', function () {
        if (viewport.scrollLeft || viewport.scrollTop) { viewport.scrollLeft = 0; viewport.scrollTop = 0; }
      });

      /* ── Pointer input: pan + pinch ───────────────────────────── */
      var pointers = {};              /* id → {x,y} (viewport-relative) */
      var dragging = false, dragMoved = false, suppressClick = false;
      var dragStart = null;           /* {x,y,camX,camY} */
      var lastMove = { x: 0, y: 0, t: 0 };
      var pinch = null;               /* {d0,z0,wx,wy} */

      function ids() { return Object.keys(pointers); }

      function rel(e) {
        var r = viewport.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      }

      function beginDrag(pt) {
        dragging = true; dragMoved = false;
        dragStart = { x: pt.x, y: pt.y, camX: cam.x, camY: cam.y };
        lastMove = { x: pt.x, y: pt.y, t: performance.now() };
        stopMotion(); anchor = null;
      }

      function beginPinch() {
        var k = ids();
        var a = pointers[k[0]], b = pointers[k[1]];
        var cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
        pinch = {
          d0: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
          z0: cam.z,
          wx: (cx - cam.x) / cam.z,
          wy: (cy - cam.y) / cam.z
        };
        dragging = false;
        stopMotion(); anchor = null;
        viewport.classList.add('is-dragging');
        suppressClick = true;
      }

      viewport.addEventListener('pointerdown', function (e) {
        safeRun('pointerdown', function () {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          pointers[e.pointerId] = rel(e);
          var n = ids().length;
          if (n === 1) beginDrag(pointers[e.pointerId]);
          else if (n === 2) beginPinch();
        });
      });

      viewport.addEventListener('pointermove', function (e) {
        safeRun('pointermove', function () {
          if (!pointers[e.pointerId]) return;
          var pt = rel(e);
          pointers[e.pointerId] = pt;

          if (pinch && ids().length >= 2) {
            var k = ids();
            var a = pointers[k[0]], b = pointers[k[1]];
            var d = Math.hypot(a.x - b.x, a.y - b.y);
            var cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
            var z = clamp(pinch.z0 * (d / pinch.d0), minZoom(), ZOOM_MAX);
            cam.z = zTarget = z;
            cam.x = cx - pinch.wx * z;    /* the point under your fingers stays put… */
            cam.y = cy - pinch.wy * z;    /* …and moving both fingers pans too */
            clampCam(); applyCam(); markMoving(); hideHint();
            return;
          }

          if (!dragging) return;
          var dx = pt.x - dragStart.x, dy = pt.y - dragStart.y;
          if (!dragMoved && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
            dragMoved = true;
            suppressClick = true;
            viewport.classList.add('is-dragging');
            try { viewport.setPointerCapture(e.pointerId); } catch (err) {}
            hideHint();
          }
          if (!dragMoved) return;

          cam.x = dragStart.camX + dx;
          cam.y = dragStart.camY + dy;
          clampCam(); applyCam(); markMoving();

          /* instantaneous velocity off the last few ms → natural flick */
          var now = performance.now();
          var dt = now - lastMove.t;
          if (dt > 0) {
            velX = (pt.x - lastMove.x) / dt;
            velY = (pt.y - lastMove.y) / dt;
          }
          lastMove = { x: pt.x, y: pt.y, t: now };
        });
      });

      function endPointer(e) {
        safeRun('pointerup', function () {
          if (!pointers[e.pointerId]) return;
          delete pointers[e.pointerId];
          try { viewport.releasePointerCapture(e.pointerId); } catch (err) {}
          var left = ids();

          if (pinch) {
            if (left.length < 2) {
              pinch = null;
              velX = 0; velY = 0;
              if (left.length === 1) beginDrag(pointers[left[0]]); /* finger still down → resume panning */
              else viewport.classList.remove('is-dragging');
            }
            setTimeout(function () { suppressClick = false; }, 60);
            return;
          }

          if (dragging && left.length === 0) {
            dragging = false;
            viewport.classList.remove('is-dragging');
            if (dragMoved && !reducedMotion && (Math.abs(velX) > 0.02 || Math.abs(velY) > 0.02)) {
              /* a long pause before release means "not a flick" */
              if (performance.now() - lastMove.t > 90) { velX = 0; velY = 0; }
              else kick();
            } else { velX = 0; velY = 0; }
            setTimeout(function () { suppressClick = false; }, 60);
          }
        });
      }
      viewport.addEventListener('pointerup', endPointer);
      viewport.addEventListener('pointercancel', endPointer);
      viewport.addEventListener('lostpointercapture', endPointer);

      /* A drag that started on a card must not also "click" it. */
      viewport.addEventListener('click', function (e) {
        if (suppressClick) { e.stopPropagation(); e.preventDefault(); }
      }, true);

      /* ── Wheel / trackpad-pinch zoom (wheel = zoom, like a map) ── */
      viewport.addEventListener('wheel', function (e) {
        safeRun('wheel', function () {
          e.preventDefault();
          var r = viewport.getBoundingClientRect();
          var dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 100 : e.deltaY;
          var k = e.ctrlKey ? 0.010 : 0.0016;      /* pinch gestures send small ctrl+wheel deltas */
          zoomTo(zTarget * Math.exp(-dy * k), e.clientX - r.left, e.clientY - r.top);
        });
      }, { passive: false });

      /* ── Zoom HUD buttons ─────────────────────────────────────── */
      function bind(id, fn) {
        var el = document.getElementById(id);
        if (el) el.addEventListener('click', function () { safeRun(id, fn); });
      }
      bind('lfjZoomIn',    function () { zoomTo(zTarget * ZOOM_STEP); });
      bind('lfjZoomOut',   function () { zoomTo(zTarget / ZOOM_STEP); });
      bind('lfjZoomReset', function () { zoomTo(1); });
      bind('lfjZoomHome',  function () { goHome(); });

      /* ── Keyboard (viewport focused): arrows pan, + - zoom, 0 reset, H home ── */
      viewport.addEventListener('keydown', function (e) {
        safeRun('viewport-keys', function () {
          var step = 220;
          var k = e.key;
          if (k === '+' || k === '=') { zoomTo(zTarget * ZOOM_STEP); }
          else if (k === '-' || k === '_') { zoomTo(zTarget / ZOOM_STEP); }
          else if (k === '0') { zoomTo(1); }
          else if (k === 'h' || k === 'H') { goHome(); }
          else if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown') {
            var nx = cam.x, ny = cam.y;
            if (k === 'ArrowLeft')  nx += step;
            if (k === 'ArrowRight') nx -= step;
            if (k === 'ArrowUp')    ny += step;
            if (k === 'ArrowDown')  ny -= step;
            flyTo(nx, ny, cam.z, 180); hideHint();
          } else { return; }
          e.preventDefault();
        });
      });

      /* Tabbing to an off-screen card should bring it into view. */
      nodesEl.addEventListener('focusin', function (e) {
        safeRun('focusin', function () {
          var node = e.target.closest && e.target.closest('.lfj-lfj-node');
          if (!node || !cameraReady) return;
          var nx = node.offsetLeft + node.offsetWidth / 2;
          var ny = node.offsetTop + node.offsetHeight / 2;
          var sx = cam.x + nx * cam.z, sy = cam.y + ny * cam.z;
          var s = vp(), pad = 60;
          if (sx < pad || sy < pad || sx > s.w - pad || sy > s.h - pad) {
            var t = camFor(nx, ny, cam.z);
            flyTo(t.x, t.y, cam.z, 380); hideHint();
          }
        });
      });

      /* ── Layout helpers ───────────────────────────────────────── */
      function setNodeCentre(el, cx, cy) {
        el.style.left = Math.round(cx - el.offsetWidth / 2) + 'px';
        el.style.top  = Math.round(cy - el.offsetHeight / 2) + 'px';
      }

      function fits(x, y, w, h, placed, m) {
        for (var i = 0; i < placed.length; i++) {
          var p = placed[i];
          if (Math.abs(x - p.x) < (w + p.w) / 2 + m && Math.abs(y - p.y) < (h + p.h) / 2 + m) return false;
        }
        return true;
      }

      /* Random cluster placement with real rectangle collision. Biggest
         nodes go first (they're hardest to fit). If the margin can't be
         honoured after many tries it relaxes gradually, and as a last
         resort places anyway — the room must never fail to render. */
      function scatter(items, occupied) {
        var placed = occupied.slice();
        items.slice().sort(function (a, b) { return b.w * b.h - a.w * a.h; }).forEach(function (it) {
          var margin = NODE_MARGIN, tries = 0, pos = null;
          while (!pos) {
            tries++;
            /* two averaged randoms → gentle pull toward the centre */
            var u = (Math.random() + Math.random()) / 2;
            var v = (Math.random() + Math.random()) / 2;
            var x = clamp(CX + (u * 2 - 1) * RX, it.w / 2 + 60, CANVAS_W - it.w / 2 - 60);
            var y = clamp(CY + (v * 2 - 1) * RY, it.h / 2 + 60, CANVAS_H - it.h / 2 - 60);
            if (fits(x, y, it.w, it.h, placed, margin) || tries > 4000) pos = { x: x, y: y };
            else if (tries % 400 === 0) margin = Math.max(16, margin * 0.85);
          }
          it.x = pos.x; it.y = pos.y;
          placed.push({ x: pos.x, y: pos.y, w: it.w, h: it.h });
        });
      }

      /* ── Web lines ────────────────────────────────────────────── */
      function relForTypes(a, b) {
        if (a === 'hero' || b === 'hero') return 'feature';
        /* CP70.8 — a teaser is "you're connected to a way out of the room,"
           a distinct relationship from anything content-to-content, so it
           gets its own line style (see .lf-web-teaser in lfj-lfj.css)
           regardless of what's on the other end. */
        if (a === 'teaser' || b === 'teaser') return 'teaser';
        if (a === 'text' && b === 'text') return 'thread';
        if (a === 'text' || b === 'text') return 'context';
        return 'related';
      }

      function buildWeb(pts) {
        var lines = [], n = pts.length;
        for (var i = 0; i < n; i++) {
          var ds = [];
          for (var j = 0; j < n; j++) {
            if (j === i) continue;
            ds.push({ j: j, d: Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y) });
          }
          ds.sort(function (a, b) { return a.d - b.d; });
          ds.slice(0, CONNECT_K).forEach(function (nb) {
            if (i < nb.j) {
              lines.push({
                x1: pts[i].x, y1: pts[i].y, x2: pts[nb.j].x, y2: pts[nb.j].y,
                rel: relForTypes(pts[i].type, pts[nb.j].type)
              });
            }
          });
        }
        return lines;
      }

      function renderWeb(lines) {
        if (!webSvg || !lines.length) return;
        webSvg.innerHTML = lines.map(function (l) {
          return '<line class="lf-web-' + l.rel + '" x1="' + Math.round(l.x1) + '" y1="' + Math.round(l.y1)
            + '" x2="' + Math.round(l.x2) + '" y2="' + Math.round(l.y2) + '"/>';
        }).join('');
      }

      /* ── Cards ────────────────────────────────────────────────── */
      /* ── Node "personality" (CP70.8, §3 of the LFJ creative direction) ──
         Was a single tiltFor(key) hash (same technique as JTV's
         per-episode colours) giving every card a deterministic -6..6deg
         tilt. Kept exactly, folded into the bundle below as its `tilt`
         field, so nothing that already depended on stable per-item tilt
         changes.
         Extends tiltFor()'s exact hash-per-key idea into a small bundle of
         idle-motion properties, so every node gets a believable "always
         moving" wobble/drift that's stable per node but different across
         nodes — never the literal Math.random()-every-load the direction
         doc explicitly ruled out.
         Everything here lands as CSS custom props consumed by
         lfj-lfj.css's `.lfj-anim-on` rule (transform/opacity-only,
         GPU-composited — no width/height/top/left animated). */
      function hash32(key) {
        var h = 0, s = String(key || '');
        for (var i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) >>> 0; }
        return h;
      }

      function personalityFor(key) {
        var h = hash32(key);
        /* Spread several independent-ish values out of one hash by
           reading different bit ranges / moduli of it — cheap, and
           avoids needing 4-5 separate hash passes per node. */
        var tilt       = (h % 13) - 6;                 /* -6..6 deg   — same as tiltFor */
        var wobbleAmp  = 1.2 + ((h >>> 4) % 26) / 10;   /* 1.2..3.7 deg */
        var wobbleDur  = 5 + ((h >>> 9) % 5);           /* 5..9s — never in sync */
        var driftPx    = 3 + ((h >>> 13) % 6);          /* 3..8px */
        var driftDur   = 7 + ((h >>> 17) % 6);          /* 7..12s */
        var delay      = -((h >>> 21) % 8000) / 1000;   /* negative delay: starts mid-cycle, not lockstep */
        return {
          tilt: tilt, wobbleAmp: wobbleAmp, wobbleDur: wobbleDur,
          driftPx: driftPx, driftDur: driftDur, delay: delay
        };
      }

      function personalityStyle(key) {
        var p = personalityFor(key);
        return '--lfj-card-tilt:' + p.tilt + 'deg;'
          + '--lfj-wobble-amp:' + p.wobbleAmp.toFixed(1) + 'deg;'
          + '--lfj-wobble-dur:' + p.wobbleDur + 's;'
          + '--lfj-drift-px:' + p.driftPx + 'px;'
          + '--lfj-drift-dur:' + p.driftDur + 's;'
          + '--lfj-anim-delay:' + p.delay.toFixed(2) + 's;';
      }

      /* ── Idle-motion cap + on/off-screen gating (CP70.8, §3) ──────
         "constantly moving" and "performs on mobile" are in tension —
         gate the wobble/drift animation to a hard cap of simultaneously
         animating nodes, and only ever animate nodes actually on
         screen. Off-cap / off-screen nodes just keep their static tilt.
         Whichever nodes are on-screen get first claim on the cap, so
         it's never visually obvious that a node "doesn't move." */
      var ANIM_CAP = 18;
      var animObserver = null;
      var onScreenNodes = [];

      function refreshAnimCap() {
        var animating = 0;
        onScreenNodes.forEach(function (el) {
          if (animating < ANIM_CAP) {
            el.classList.add('lfj-anim-on');
            animating++;
          } else {
            el.classList.remove('lfj-anim-on');
          }
        });
      }

      function initAnimObserver(nodeEls) {
        if (reducedMotion || !nodeEls.length) return;   /* prefers-reduced-motion kills this entirely */
        if (!('IntersectionObserver' in window)) {
          /* No IO support: fall back to "always on" up to the cap rather
             than never animating at all. */
          nodeEls.slice(0, ANIM_CAP).forEach(function (el) { el.classList.add('lfj-anim-on'); });
          return;
        }
        animObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var el = entry.target;
            var idx = onScreenNodes.indexOf(el);
            if (entry.isIntersecting) {
              if (idx === -1) onScreenNodes.push(el);
            } else {
              el.classList.remove('lfj-anim-on');
              if (idx > -1) onScreenNodes.splice(idx, 1);
            }
          });
          refreshAnimCap();
        }, { root: viewport, threshold: 0.1 });
        nodeEls.forEach(function (el) { animObserver.observe(el); });
      }

      var currentVideos = [];

      /* Checkpoint 60: renders either a video card (play-button overlay)
         or a picture card (plain image, small camera badge instead —
         nothing to "play"), plus an attached pixel dialogue bubble
         when the entry carries a `thought` — the LFJ diary feature.
         No thought → no bubble, exactly the video/picture alone. */
      function buildThoughtBubbleHTML(thought) {
        if (!thought) return '';
        return '<span class="lfj-lfj-thought-bubble" aria-hidden="false">'
          + '<span class="lfj-lfj-thought-bubble-text">' + escHTML(thought) + '</span>'
          + '<span class="lfj-lfj-thought-bubble-tail" aria-hidden="true"></span>'
          + '</span>';
      }

      function buildCardHTML(item, idx) {
        var isImage = item.kind === 'image';
        var hasThumb = item.thumbnail && item.thumbnail.length > 0;
        var imgSrc = hasThumb ? item.thumbnail : (isImage && item.src ? item.src : '');
        var hasImgSrc = imgSrc && imgSrc.length > 0;
        var thumbImg = hasImgSrc
          ? '<img src="' + escAttr(imgSrc) + '" alt="' + escAttr(item.title || '') + '" loading="lazy" draggable="false">'
          : '<img data-need-thumb="1" data-idx="' + idx + '" alt="' + escAttr(item.title || '') + '" loading="lazy" draggable="false">';
        var sig = isImage ? 'PHOTO' : (item.type || item.section || 'CLIP');
        var overlayMark = isImage
          ? '<span class="lfj-lfj-card-photo-badge" aria-hidden="true">' + pixelIconSVG('camera', 14) + '</span>'
          : '<span class="lfj-lfj-card-play" aria-hidden="true">' + pixelIconSVG('play', 22) + '</span>';
        var kindCls = isImage ? ' lfj-lfj-node--picture' : '';
        return '<button type="button" class="lfj-lfj-node lfj-lfj-node--card' + kindCls + '" data-idx="' + idx + '"'
          + ' style="' + escAttr(personalityStyle(item.key || item.id || item.title || idx)) + '"'
          + ' aria-label="' + (isImage ? 'View: ' : 'Play: ') + escAttr(item.title || (isImage ? 'photo' : 'video')) + '">'
          + '<span class="lfj-lfj-node-heading">' + escHTML(sig) + '</span>'
          + '<span class="lfj-lfj-card-thumb' + (hasImgSrc ? '' : ' lfj-lfj-card-thumb--empty') + '">' + thumbImg
          + overlayMark + '</span>'
          + '<span class="lfj-lfj-card-bar"><span class="lfj-lfj-card-title">' + escHTML(item.title || '') + '</span></span>'
          + buildThoughtBubbleHTML(item.thought)
          + '</button>';
      }

      /* LFJ-destined entries first (so anything published to this room
         from the bot can't be cut off), then everything else, capped.
         Checkpoint 59 — no longer requires a thumbnail to appear here;
         assets/lfj-thumb-fallback.js fills one in at render time.
         Checkpoint 60 — now merges gallery.json (pictures) alongside
         videos.json, since LFJ supports pictures too, not just videos.
         Every item is tagged `.kind` ('video' | 'image') so downstream
         rendering/click-handling can branch correctly. */
      function pickItems(videos, gallery) {
        var seen = {}, lfjTagged = [], rest = [];
        function tag(v, kind) {
          if (!v) return null;
          var k = kind + ':' + (v.key || v.id || v.title);
          if (seen[k]) return null;
          seen[k] = true;
          v.kind = kind;
          return v;
        }
        (videos || []).forEach(function (v) {
          /* Checkpoint 65 — the bot allows a text-only "Thought of the Moment"
             (no video/link at all). That belongs in the status feed; on the
             canvas it would be an empty video card. */
          if (!v || (!v.videoUrl && !v.src && !v.igUrl && !v.id)) return;
          var t = tag(v, 'video');
          if (!t) return;
          if (Array.isArray(v.destination) && v.destination.indexOf('lfj') > -1) lfjTagged.push(t);
          else rest.push(t);
        });
        (gallery || []).forEach(function (v) {
          var t = tag(v, 'image');
          if (t && Array.isArray(v.destination) && v.destination.indexOf('lfj') > -1) lfjTagged.push(t);
          /* pictures with no LFJ destination stay out entirely —
             Portfolio is their home, not the "everything else" pool
             legacy undestined videos fall into below. */
        });
        /* Same all-or-nothing fallback as before Checkpoint 60: only
           show unrelated content if there is truly nothing LFJ-tagged
           yet. Once real tagged content exists, the room shows only
           that — it doesn't get diluted with random padding. */
        var out = lfjTagged.length ? lfjTagged : rest.slice(0, MAX_CARDS);
        return out.slice(0, MAX_CARDS);
      }

      /* ── Text nodes (statement + tagline) ─────────────────────── */
      function makeTextNode(cls, eyebrow, text) {
        var el = document.createElement('div');
        el.className = 'lfj-lfj-node lfj-lfj-node--text ' + cls;
        el.innerHTML = (eyebrow ? '<span class="lfj-lfj-text-label">' + escHTML(eyebrow) + '</span>' : '')
          + '<span class="lfj-lfj-text-body">' + escHTML(text) + '</span>';
        nodesEl.appendChild(el);
        return el;
      }

      /* ── Extra content nodes: text + teaser (CP70.8) ─────────────
         Built from assets/lfj-extras.json (window.LF_CONFIG.extras),
         merged onto the canvas alongside video/image cards and the
         hardcoded statement/tagline text nodes above. See §1.3/1.4 of
         the LFJ creative direction doc for the two node kinds. */
      function buildExtraTextNode(entry) {
        var cls = entry.style === 'quote' ? 'lfj-lfj-extra-text--quote' : 'lfj-lfj-extra-text--statement';
        var el = document.createElement('div');
        el.className = 'lfj-lfj-node lfj-lfj-node--extra-text ' + cls;
        el.setAttribute('style', personalityStyle(entry.id || entry.text));
        /* CP75 — NEW badge: entry.new === true, or entry.date (YYYY-MM-DD) within the last 7 days. */
        var isNew = entry['new'] === true;
        if (!isNew && entry.date) { var d = Date.parse(entry.date); if (!isNaN(d) && (Date.now() - d) < 7 * 864e5 && d <= Date.now() + 864e5) isNew = true; }
        el.innerHTML = (isNew ? '<span class="lfj-lfj-new-badge" aria-label="New note">&#9733; NEW NOTE</span>' : '')
          + '<span class="lfj-lfj-extra-text-body">' + escHTML(entry.text || '') + '</span>';
        if (isNew) el.classList.add('lfj-lfj-node--is-new');
        nodesEl.appendChild(el);
        return el;
      }

      /* A teaser navigates away (real <a href>, not the modal) — see
         §5.2 of the direction doc: the whole point of a teaser is
         sending someone somewhere else, so it's the one node type
         where a click should leave the canvas. */
      function buildExtraTeaserNode(entry) {
        var accentVar = '--lfj-room-' + (entry.accent || 'lfj');
        var el = document.createElement('a');
        el.className = 'lfj-lfj-node lfj-lfj-node--teaser';
        el.href = entry.href || '#';
        el.setAttribute('style', personalityStyle(entry.id || entry.line) + '--lfj-teaser-accent:var(' + accentVar + ', var(--lfj-room-lfj));');
        el.innerHTML = '<span class="lfj-lfj-teaser-icon" aria-hidden="true">' + pixelIconSVG(entry.icon, 20) + '</span>'
          + '<span class="lfj-lfj-teaser-line">' + escHTML(entry.line || '') + '</span>'
          + '<span class="lfj-lfj-teaser-btn">' + escHTML(entry.button || 'GO') + ' \u2192</span>';
        nodesEl.appendChild(el);
        return el;
      }

      function buildExtraNodes(extras) {
        var items = [];
        (extras || []).forEach(function (entry) {
          if (!entry || !entry.type) return;
          if (entry.type === 'text' && entry.text) {
            items.push({ el: buildExtraTextNode(entry), type: 'text' });
          } else if (entry.type === 'teaser' && entry.href) {
            items.push({ el: buildExtraTeaserNode(entry), type: 'teaser' });
          }
        });
        return items;
      }

      /* ── Standalone canvas headings ─────────────────────────────
         Plain non-interactive labels placed straight on the canvas
         (kept from checkpoint 28), scaled to the wider layout. */
      function renderCanvasHeadings() {
        [
          { text: 'THE ARCHIVE', dx: -RX * 0.85, dy: -RY * 0.80 },
          { text: 'WATCH',       dx:  RX * 0.90, dy: -RY * 0.70 },
          { text: 'THE CULTURE', dx: -RX * 0.90, dy:  RY * 0.85 },
          { text: 'CONNECT',     dx:  RX * 0.85, dy:  RY * 0.90 }
        ].forEach(function (h) {
          var el = document.createElement('div');
          el.className = 'lfj-lfj-canvas-heading';
          el.textContent = h.text;
          el.style.left = Math.round(CX + h.dx) + 'px';
          el.style.top  = Math.round(CY + h.dy) + 'px';
          nodesEl.appendChild(el);
        });
      }

      /* ── Persistent social bar (real pixel icons, from checkpoint 28) ── */
      var PIXEL_ICONS = {
        youtube:   ['........', '..#.....', '..##....', '..###...', '..####..', '..###...', '..##....', '..#.....'],
        instagram: ['.######.', '#......#', '#.####.#', '#.#..#.#', '#.#..#.#', '#.####.#', '#......#', '.######.'],
        tiktok:    ['.....##.', '.....##.', '.....##.', '.....##.', '.....##.', '.####.#.', '#....##.', '.####...'],
        x:         ['#......#', '.#....#.', '..#..#..', '...##...', '...##...', '..#..#..', '.#....#.', '#......#'],
        discord:   ['.######.', '#......#', '#.##.##.', '#......#', '#.#..#.#', '#......#', '.#....#.', '..####..'],
        /* Checkpoint 61 (4h) — card overlays, replacing the CSS-drawn
           border-triangle play mark and rounded-rect camera badge */
        play:      ['#.......', '###.....', '#####...', '#######.', '#######.', '#####...', '###.....', '#.......'],
        camera:    ['........', '.##.....', '########', '#......#', '#..##..#', '#..##..#', '#......#', '########'],
        /* CP70.8 — teaser-card icons, one per cross-promotion target.
           Same 8x8/currentColor convention as the set above, kept small
           and abstract like `play`/`camera` rather than borrowing the
           16x16 main-menu icons (snippets/lfj-pixel-icon.liquid), since
           these render from JS-built strings, not Liquid. */
        listen:    ['...#....', '..##..#.', '####...#', '####.#.#', '####.#.#', '####...#', '..##..#.', '...#....'],
        bag:       ['..####..', '.#....#.', '.#....#.', '########', '#......#', '#......#', '#......#', '########'],
        tv:        ['########', '#......#', '#.####.#', '#.####.#', '#.####.#', '#......#', '...##...', '..####..'],
        cassette:  ['########', '#......#', '#.##.##.', '#.#..#.#', '#.#..#.#', '#.##.##.', '#......#', '########'],
        mail:      ['########', '#......#', '#.#..#.#', '#..##..#', '#.#..#.#', '#......#', '#......#', '########'],
        portrait:  ['..####..', '.#....#.', '.#....#.', '..####..', '.#....#.', '#......#', '#......#', '########']
      };

      function pixelIconSVG(key, size) {
        var rows = PIXEL_ICONS[key];
        if (!rows) return '';
        size = size || 14;
        var cells = '';
        for (var y = 0; y < rows.length; y++) {
          for (var x = 0; x < rows[y].length; x++) {
            if (rows[y].charAt(x) === '#') cells += '<rect x="' + x + '" y="' + y + '" width="1" height="1"/>';
          }
        }
        return '<svg class="lfj-lfj-pixel-icon" viewBox="0 0 8 8" width="' + size + '" height="' + size + '"'
          + ' aria-hidden="true" fill="currentColor" shape-rendering="crispEdges">' + cells + '</svg>';
      }

      function collectSocialLinks() {
        var d = rootEl.dataset;
        return [
          { key: 'youtube',   label: 'YOUTUBE',   url: d.socialYoutube },
          { key: 'instagram', label: 'INSTAGRAM', url: d.socialInstagram },
          { key: 'tiktok',    label: 'TIKTOK',    url: d.socialTiktok },
          { key: 'x',         label: 'X',         url: d.socialX },
          { key: 'discord',   label: 'DISCORD',   url: d.socialDiscord }
        ].filter(function (l) { return l.url && l.url.length > 0; });
      }

      function renderUtilityBar() {
        var links = collectSocialLinks();
        if (!links.length) return;
        var bar = document.createElement('div');
        bar.className = 'lfj-lfj-utility-bar';
        bar.innerHTML = links.map(function (l) {
          return '<a class="lfj-lfj-util-btn" href="' + escAttr(l.url) + '" target="_blank" rel="noopener" data-click-sfx'
            + ' aria-label="' + escAttr(l.label) + ' (opens in a new tab)">' + pixelIconSVG(l.key) + '</a>';
        }).join('');
        stageEl.appendChild(bar);
      }

      /* ── Modal — playback via the shared multi-source engine ──── */
      var lastFocusEl = null;

      function openModal(item) {
        safeRun('openModal', function () {
          if (!item) return;
          if (item.kind === 'image') {
            /* Checkpoint 60: pictures reuse the same modal chrome
               (close button, title/year bar) but show a plain image
               instead of mounting LFJ_THEATER_PLAYER, which is a
               video-only engine. */
            if (!modal || !modalBody) {
              var imgOut = item.src || item.thumbnail;
              if (imgOut) window.open(imgOut, '_blank', 'noopener');
              return;
            }
            lastFocusEl = document.activeElement;
            modalBody.innerHTML = '<img class="lfj-lfj-modal-image" src="'
              + escAttr(item.src || item.thumbnail || '') + '" alt="' + escAttr(item.title || '') + '">';
            if (modalTitle) modalTitle.textContent = item.title || '';
            if (modalYear) modalYear.textContent = item.year || '';
            modal.removeAttribute('hidden');
            modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
            if (modalClose) modalClose.focus();
            return;
          }
          if (!modal || !modalBody || !window.LFJ_THEATER_PLAYER) {
            /* no player available → don't fail silently */
            var out = item.igUrl || item.videoUrl;
            if (out) window.open(out, '_blank', 'noopener');
            return;
          }
          var itemUrl = item.videoUrl || item.src || '';
          var isYouTube = /youtu\.?be/.test(itemUrl);
          var idMatch = isYouTube ? itemUrl.match(/(?:youtu\.be\/|v=)([\w-]+)/) : null;
          lastFocusEl = document.activeElement;
          window.LFJ_THEATER_PLAYER.mount(modalBody, {
            title: item.title || '',
            youtubeId: idMatch ? idMatch[1] : (item.id || ''),
            websiteVideoUrl: (!isYouTube && itemUrl) ? itemUrl : '',
            igUrl: item.igUrl || ''
          });
          if (modalTitle) modalTitle.textContent = item.title || '';
          if (modalYear) modalYear.textContent = item.year || '';
          modal.removeAttribute('hidden');
          modal.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
          if (modalClose) modalClose.focus();
        });
      }

      function closeModal() {
        safeRun('closeModal', function () {
          if (!modal) return;
          if (window.LFJ_THEATER_PLAYER && modalBody) window.LFJ_THEATER_PLAYER.unmount(modalBody);
          modalBody.innerHTML = ''; /* clears any picture left mounted by openModal's image branch */
          modal.setAttribute('hidden', '');
          modal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
          if (lastFocusEl && lastFocusEl.focus) { try { lastFocusEl.focus({ preventScroll: true }); } catch (e) {} }
        });
      }
      function modalIsOpen() { return !!(modal && !modal.hasAttribute('hidden')); }

      if (modalClose) modalClose.addEventListener('click', closeModal);
      if (modal) modal.addEventListener('click', function (e) { if (e.target === modal) closeModal(); });
      document.addEventListener('keydown', function (e) {
        safeRun('keydown', function () {
          if (e.key === 'Escape' && modalIsOpen()) closeModal();
        });
      });

      /* Exposed for the taskbar widget (same file, second init block) */
      window.__lfjLfjOpen = openModal;

      /* ── Notify toast ─────────────────────────────────────────── */
      function scheduleNotify() {
        safeRun('scheduleNotify', function () {
          if (rootEl.dataset.notifyEnabled === 'false') return;
          try { if (sessionStorage.getItem('lfLfjNotified')) return; } catch (e) {}
          var msg = rootEl.dataset.notifyMessage || 'WELCOME TO THE CULTURE.';
          setTimeout(function () {
            safeRun('notify', function () {
              if (typeof window.LFJ_NOTIFY === 'function') {
                window.LFJ_NOTIFY(msg);
                try { sessionStorage.setItem('lfLfjNotified', '1'); } catch (e) {}
              }
            });
          }, NOTIFY_DELAY_MS);
        });
      }

      /* ── Boot: build nodes → measure → scatter → web → camera ── */
      function boot() {
        safeRun('boot', function () {
          var videos = (window.LF_CONFIG && Array.isArray(window.LF_CONFIG.videos)) ? window.LF_CONFIG.videos : [];
          var gallery = (window.LF_CONFIG && Array.isArray(window.LF_CONFIG.gallery)) ? window.LF_CONFIG.gallery : [];
          currentVideos = pickItems(videos, gallery);

          /* Cards */
          nodesEl.insertAdjacentHTML('beforeend', currentVideos.map(buildCardHTML).join(''));
          /* Checkpoint 59 — fill in a random-frame/YouTube thumbnail for
             any card that had no thumbnail value (see buildCardHTML
             above and assets/lfj-thumb-fallback.js). */
          if (window.LFJ_THUMB_FALLBACK) {
            nodesEl.querySelectorAll('img[data-need-thumb]').forEach(function (img) {
              var idx = parseInt(img.getAttribute('data-idx'), 10);
              var entry = currentVideos[idx];
              if (entry) window.LFJ_THUMB_FALLBACK.apply(img, entry);
            });
          }
          var items = [];
          nodesEl.querySelectorAll('.lfj-lfj-node--card').forEach(function (card) {
            var i = parseInt(card.dataset.idx, 10);
            card.addEventListener('click', function () {
              safeRun('card-click', function () { openModal(currentVideos[i]); });
            });
            items.push({ el: card, type: 'card' });
          });

          /* Server-rendered nodes (about blocks, image placeholders, statement) */
          nodesEl.querySelectorAll('[data-lfj-static]').forEach(function (el) {
            items.push({ el: el, type: el.getAttribute('data-lfj-static') === 'about' ? 'text' : 'card' });
          });

          /* Statement + tagline text nodes. If the Homepage Statement
             setting is rendering its own node, don't duplicate it. */
          var d = rootEl.dataset;
          var hasProclaim = !!nodesEl.querySelector('.lfj-lfj-node--proclaim');
          if (!hasProclaim && d.statement) items.push({ el: makeTextNode('', d.eyebrow || 'LIVEFASTJAYDO', d.statement), type: 'text' });
          if (d.tagline) items.push({ el: makeTextNode('lfj-lfj-node--tagline', null, d.tagline), type: 'text' });
          if (hasProclaim) items.push({ el: nodesEl.querySelector('.lfj-lfj-node--proclaim'), type: 'text' });

          /* CP70.8 — text + teaser nodes from lfj-extras.json */
          var extras = (window.LF_CONFIG && Array.isArray(window.LF_CONFIG.extras)) ? window.LF_CONFIG.extras : [];
          items = items.concat(buildExtraNodes(extras));

          /* Measure (real rendered sizes — so the mobile CSS resizes are honoured) */
          items.forEach(function (it) {
            it.w = it.el.offsetWidth || 160;
            it.h = it.el.offsetHeight || 100;
            if (it.type === 'card' && it.el.classList.contains('lfj-lfj-node--card')) it.h += 16; /* heading above the box */
          });

          /* Hero is fixed at the centre; reserve its rectangle (+ the REC label below it) */
          var hw = heroEl ? heroEl.offsetWidth : 300;
          var hh = heroEl ? heroEl.offsetHeight : 169;
          if (heroEl) setNodeCentre(heroEl, CX, CY);
          scatter(items, [{ x: CX, y: CY + 14, w: hw + 24, h: hh + 40 }]);

          items.forEach(function (it) {
            /* real cards carry a heading above the box, so nudge them down half of it */
            setNodeCentre(it.el, it.x, it.y + (it.el.classList.contains('lfj-lfj-node--card') ? 8 : 0));
          });

          renderWeb(buildWeb([{ x: CX, y: CY, type: 'hero' }].concat(items.map(function (it) { return { x: it.x, y: it.y, type: it.type }; }))));
          renderCanvasHeadings();
          renderUtilityBar();

          /* CP70.8 — idle-motion cap/gating, wired up after final layout
             so it observes nodes at their real scattered position. */
          initAnimObserver(items.map(function (it) { return it.el; }));

          if (!cameraReady) initCamera();   /* ResizeObserver covers the case where size arrives late */
          scheduleNotify();
        });
      }

      var readyPromises = [];
      if (window.LF_VIDEOS_READY) readyPromises.push(window.LF_VIDEOS_READY);
      if (window.LF_GALLERY_READY) readyPromises.push(window.LF_GALLERY_READY);
      if (window.LF_EXTRAS_READY) readyPromises.push(window.LF_EXTRAS_READY);
      if (readyPromises.length) {
        Promise.all(readyPromises).then(boot).catch(function () { boot(); });
      } else {
        boot();
      }
    });
  });

  /* ================================================================
     TASKBAR — in-screen dock: drag/tap handle, animated + sounded
     shortcuts, "latest drop" widget.
     Web Audio SFX pattern matches lfj-menu-icons.js exactly
     (original synthesized tones, not copied game audio).
  ================================================================ */
  onReady(function () {
    safeRun('taskbar init', function () {
      var taskbar = document.getElementById('lfjTaskbar');
      var handle  = document.getElementById('lfjTaskbarHandle');
      var panel   = document.getElementById('lfjTaskbarPanel');
      if (!taskbar || !handle || !panel) return;

      var isOpen = false;

      function getAudioCtx() {
        try {
          var AC = window.AudioContext || window.webkitAudioContext;
          if (!AC) return null;
          if (!window.__lfjSharedAudioCtx) window.__lfjSharedAudioCtx = new AC();
          var ctx = window.__lfjSharedAudioCtx;
          if (ctx.state === 'suspended') ctx.resume().catch(function () {});
          return ctx;
        } catch (e) { return null; }
      }

      function tone(ctx, freq, type, startAt, dur, peak) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        var now = ctx.currentTime + startAt;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(peak, now + 0.008);
        gain.gain.linearRampToValueAtTime(0, now + dur);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(now); osc.stop(now + dur + 0.02);
      }

      function playSeq(seq) {
        try {
          var ctx = getAudioCtx();
          if (!ctx) return;
          seq.forEach(function (n) { tone(ctx, n[0], n[1], n[2], n[3], 0.08); });
        } catch (e) {}
      }

      var HANDLE_OPEN_SFX  = [[440, 'square', 0, .05], [660, 'square', .05, .06]];
      var HANDLE_CLOSE_SFX = [[660, 'square', 0, .04], [440, 'square', .04, .06]];
      var TASKBTN_SFX = {
        videos:     [[300, 'sawtooth', 0, .04], [500, 'sawtooth', .04, .05]],
        about:      [[523, 'triangle', 0, .05], [659, 'triangle', .05, .05], [784, 'triangle', .1, .06]],
        portfolio:  [[900, 'square', 0, .02], [700, 'square', .03, .03]],
        commission: [[784, 'sine', 0, .04], [1046, 'sine', .04, .05]]
      };

      function setOpen(open, quiet) {
        if (open === isOpen) return;
        isOpen = open;
        taskbar.classList.toggle('is-open', open);
        handle.setAttribute('aria-expanded', open ? 'true' : 'false');
        handle.setAttribute('aria-label', open ? 'Close shortcuts' : 'Open shortcuts');
        if (!quiet) playSeq(open ? HANDLE_OPEN_SFX : HANDLE_CLOSE_SFX);
      }

      /* Tap toggles; drag up opens, drag down closes. Pointer capture so
         a drag that slides off the handle still finishes. */
      var dragY0 = null, dragged = false;
      handle.addEventListener('pointerdown', function (e) {
        dragY0 = e.clientY; dragged = false;
        try { handle.setPointerCapture(e.pointerId); } catch (err) {}
      });
      handle.addEventListener('pointermove', function (e) {
        if (dragY0 == null) return;
        var dy = dragY0 - e.clientY;
        if (dy > 18 && !isOpen)  { setOpen(true);  dragged = true; dragY0 = null; }
        if (dy < -18 && isOpen)  { setOpen(false); dragged = true; dragY0 = null; }
      });
      handle.addEventListener('pointerup', function () { dragY0 = null; });
      handle.addEventListener('pointercancel', function () { dragY0 = null; });
      handle.addEventListener('click', function () {
        safeRun('taskbar-toggle', function () {
          if (dragged) { dragged = false; return; }   /* the drag already handled it */
          setOpen(!isOpen);
        });
      });

      /* Close on Escape, or a tap anywhere outside the dock */
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !isOpen) return;
        var m = document.getElementById('lfjLfjModal');
        if (m && !m.hasAttribute('hidden')) return;   /* the modal owns Escape first */
        setOpen(false);
        handle.focus();
      });
      document.addEventListener('pointerdown', function (e) {
        if (isOpen && !taskbar.contains(e.target)) setOpen(false);
      });

      panel.querySelectorAll('.lfj-taskbar-btn[data-taskbtn]').forEach(function (btn) {
        btn.addEventListener('pointerenter', function () {
          var key = btn.getAttribute('data-taskbtn');
          if (TASKBTN_SFX[key]) playSeq(TASKBTN_SFX[key]);
        });
      });

      /* ── "Latest drop" widget — live peek at the newest upload ── */
      safeRun('taskbar widget', function () {
        var widget    = document.getElementById('lfjTaskbarWidget');
        var widgetBtn = document.getElementById('lfjTaskbarWidgetBtn');
        var thumbEl   = document.getElementById('lfjTaskbarWidgetThumb');
        var titleEl   = document.getElementById('lfjTaskbarWidgetTitle');
        if (!widget || !window.LF_VIDEOS_READY) return;

        window.LF_VIDEOS_READY.then(function () {
          var videos = (window.LF_CONFIG && window.LF_CONFIG.videos) || [];
          var latest = videos[0];
          if (!latest) return;

          if (thumbEl && latest.thumbnail) {
            thumbEl.style.backgroundImage = 'url(' + latest.thumbnail + ')';
          } else if (thumbEl && window.LFJ_THUMB_FALLBACK) {
            window.LFJ_THUMB_FALLBACK.resolve(latest, function (url) {
              thumbEl.style.backgroundImage = 'url(' + url + ')';
            });
          }
          if (titleEl) titleEl.textContent = latest.title || '';
          widget.hidden = false;

          if (widgetBtn) {
            widgetBtn.addEventListener('click', function () {
              safeRun('widget-click', function () {
                setOpen(false, true);
                if (typeof window.__lfjLfjOpen === 'function') window.__lfjLfjOpen(latest);
              });
            });
          }
        }).catch(function () {});
      });
    });
  });
})();
